<script setup lang="ts">
import useMenuStore from '@/store/modules/menu'
import useOnboardingStore from '@/store/modules/onboarding'
import useRouteStore from '@/store/modules/route'
import useSettingsStore from '@/store/modules/settings'
import useTabbarStore from '@/store/modules/tabbar'
import useUserStore from '@/store/modules/user'
import { useRouter } from 'vue-router'
import GuideLauncher from './GuideLauncher.vue'
import GuideTaskCenter from './GuideTaskCenter.vue'
import GuideTour from './GuideTour.vue'
import GuideWelcome from './GuideWelcome.vue'

const userStore = useUserStore()
const onboardingStore = useOnboardingStore()
const menuStore = useMenuStore()
const routeStore = useRouteStore()
const settingsStore = useSettingsStore()
const tabbarStore = useTabbarStore()
const router = useRouter()

function decorateGuideAnchors() {
  const searchIcon = document.querySelector('[class~="i-ri:search-line"]')
  const notificationIcon = document.querySelector('[class~="i-ri:notification-3-line"]')
  const businessTopNavigation = document.querySelector('header .business-top-navigation')
    || document.querySelector('header .menu-container')

  // 旧版本曾把 top-nav 标识加到 Topbar、标签栏或其他工具区域。
  // 每次系统页面完成渲染后先清理错误标识，只保留真正的横向业务主导航。
  document.querySelectorAll('[data-guide-id="top-nav"]').forEach((element) => {
    if (element !== businessTopNavigation) {
      element.removeAttribute('data-guide-id')
    }
  })

  const anchors: Array<[string, Element | null]> = [
    ['top-nav', businessTopNavigation],
    ['side-nav', document.querySelector('.sub-sidebar-container')],
    ['global-search', searchIcon?.closest('button') || null],
    ['notifications', notificationIcon?.closest('button') || null],
    ['personal-menu', document.querySelector('.user-trigger')],
    ['plan-card', document.querySelector('.plan-mini-card')],
    ['quick-actions', document.querySelector('.quick-actions')],
  ]
  anchors.forEach(([id, element]) => {
    if (element && !element.hasAttribute('data-guide-id')) {
      element.setAttribute('data-guide-id', id)
    }
  })
}

function resolveWorkspacePath() {
  // 工作台路由也只使用后端接口生成的索引。
  const currentPath = router.currentRoute.value.path
  const currentRecord = routeStore.backendGuideRoutes.find(item => item.fullPath === currentPath)
  if (currentRecord) {
    return currentRecord.fullPath
  }
  return routeStore.backendGuideRoutes[0]?.fullPath || currentPath
}

function syncOnboardingContext() {
  onboardingStore.setWorkspaceRoute(resolveWorkspacePath())
  // 任务库严格使用当前版本批准的完整任务清单。
  // 路由是否能进入在具体步骤执行时判断，不能在初始化阶段删除调查站或管理员任务。
  onboardingStore.setAccessibleTaskIds(onboardingStore.edition.tasks.map(task => task.id))
}

function isVisibleElement(element: Element | null) {
  if (!(element instanceof HTMLElement)) {
    return false
  }
  const style = window.getComputedStyle(element)
  const rect = element.getBoundingClientRect()
  return style.display !== 'none'
    && style.visibility !== 'hidden'
    && Number(style.opacity || 1) > 0
    && rect.width > 0
    && rect.height > 0
}

function hasRenderedBusinessPage() {
  const main = document.querySelector('#app-main .main-container .main')
  if (!(main instanceof HTMLElement) || !isVisibleElement(main)) {
    return false
  }

  return Array.from(main.children).some((child) => {
    if (!(child instanceof HTMLElement)) {
      return false
    }
    if (child.classList.contains('exit-main-page-maximize')) {
      return false
    }
    return isVisibleElement(child)
  })
}

function waitFrame() {
  return new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve())
  })
}

function waitTime(milliseconds: number) {
  return new Promise<void>(resolve => window.setTimeout(resolve, milliseconds))
}

let systemReadyGeneration = 0

async function waitForSystemPageReady(generation: number) {
  await router.isReady()
  const startedAt = Date.now()
  let stablePath = ''
  let stableCount = 0

  while (Date.now() - startedAt < 15000) {
    if (generation !== systemReadyGeneration || !userStore.isLogin) {
      return false
    }

    await nextTick()
    await waitFrame()

    const currentRoute = router.currentRoute.value
    const routeReady = routeStore.isGenerate
      && routeStore.backendGuideRoutesStatus === 'ready'
      && currentRoute.matched.length > 0
      && currentRoute.name !== 'login'
      && !/^\/login(?:\/|$)/i.test(currentRoute.path)
    const layoutReady = isVisibleElement(document.querySelector('#app-main'))
      && isVisibleElement(document.querySelector('#app-main .main-container'))
    const navigationReady = isVisibleElement(document.querySelector('.topbar-container'))
      || isVisibleElement(document.querySelector('.sidebar-container'))
    const pageReady = hasRenderedBusinessPage()

    if (routeReady && layoutReady && navigationReady && pageReady) {
      if (stablePath === currentRoute.fullPath) {
        stableCount += 1
      }
      else {
        stablePath = currentRoute.fullPath
        stableCount = 1
      }
      // 连续多个渲染帧保持同一路由，避免登录跳转动画尚未结束时弹出。
      if (stableCount >= 3) {
        await waitTime(300)
        return generation === systemReadyGeneration
          && userStore.isLogin
          && router.currentRoute.value.fullPath === stablePath
      }
    }
    else {
      stablePath = ''
      stableCount = 0
    }

    await waitTime(100)
  }

  return false
}

watch(
  () => [
    userStore.isLogin,
    userStore.version,
    userStore.account,
    userStore.tenantId,
    userStore.permissions.join('|'),
    routeStore.isGenerate,
    routeStore.backendGuideRoutesStatus,
    routeStore.backendGuideRoutes.map(item => `${item.id}:${item.fullPath}:${item.component}`).join('|'),
    menuStore.sidebarMenusFirstDeepestPath,
    tabbarStore.list.map(item => `${item.fullPath}:${item.isPin}`).join('|'),
    settingsStore.settings.home.enable,
    settingsStore.settings.home.fullPath,
    router.currentRoute.value.fullPath,
  ] as const,
  async ([isLogin, versionId, account, tenantId, _permissions, routesGenerated, guideRoutesStatus]) => {
    const generation = ++systemReadyGeneration
    if (!isLogin) {
      onboardingStore.clearSession()
      return
    }
    onboardingStore.initialize({
      versionId: String(versionId || '3'),
      account,
      tenantId,
    })

    // 登录后先查询服务端新手引导进度。
    // 三个版本统一：自动弹出只认 GET 返回的 exists=false；GET 失败时不会误弹。
    await onboardingStore.loadRemoteProgress()
    if (generation !== systemReadyGeneration || !userStore.isLogin) {
      return
    }

    if (!routesGenerated || guideRoutesStatus !== 'ready') {
      return
    }

    const systemReady = await waitForSystemPageReady(generation)
    if (!systemReady || generation !== systemReadyGeneration) {
      return
    }

    await nextTick()
    decorateGuideAnchors()
    syncOnboardingContext()
    onboardingStore.openWelcomeWhenSystemReady()
  },
  { immediate: true },
)
</script>

<template>
  <GuideWelcome />
  <GuideTaskCenter />
  <GuideTour />
  <GuideLauncher />
</template>
